/**
 * The line icons `book.html` draws inline, as components.
 *
 * They are grouped in one file rather than given a file each because none of them is a named type
 * or a unit of behaviour: each is a `viewBox` and a path, sized and coloured entirely by the
 * stylesheet's `svg` rules, and half of them appear in three places on the page. Splitting fifteen
 * two-line functions across fifteen files would hide that they are one set.
 *
 * Every one is `aria-hidden`: each sits beside the words it illustrates, so announcing it would
 * repeat the label rather than add to it.
 */
import type { ReactNode } from 'react';

function Icon({ children }: { children: ReactNode }) {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {children}
        </svg>
    );
}

export function ChevronLeftIcon() {
    return (
        <Icon>
            <polyline points="15 18 9 12 15 6" />
        </Icon>
    );
}

export function ChevronRightIcon() {
    return (
        <Icon>
            <polyline points="9 18 15 12 9 6" />
        </Icon>
    );
}

export function CheckIcon() {
    return (
        <Icon>
            <polyline points="20 6 9 17 4 12" />
        </Icon>
    );
}

export function InfoIcon() {
    return (
        <Icon>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </Icon>
    );
}

export function ClockIcon() {
    return (
        <Icon>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </Icon>
    );
}

export function CalendarIcon() {
    return (
        <Icon>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </Icon>
    );
}

export function MapPinIcon() {
    return (
        <Icon>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </Icon>
    );
}

export function MonitorIcon() {
    return (
        <Icon>
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
        </Icon>
    );
}

export function PackageIcon() {
    return (
        <Icon>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </Icon>
    );
}

export function ShieldIcon() {
    return (
        <Icon>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </Icon>
    );
}

export function UsersIcon() {
    return (
        <Icon>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </Icon>
    );
}

export function PhoneIcon() {
    return (
        <Icon>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72" />
        </Icon>
    );
}

export function AlertIcon() {
    return (
        <Icon>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </Icon>
    );
}

export function HomeIcon() {
    return (
        <Icon>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
        </Icon>
    );
}
